<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="tillAccountRef"
      :items="mobileTillAccountList"
      :busy="isBusy"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectMobileTillAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectMobileTillAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteMobileTillAccount(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      :ok-title="buttonLabel"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="mobileTillAccountRules">
        <b-form @submit.prevent="handleSubmit">
          <h6 class="section-label mb-2 mt-2">
            {{ $t('General Information') }}
          </h6>
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="mobileTillAccountFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :set-selected="setTellerUsers"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->

            <!-- Account -->
            <b-col md="6">
              <SelectField
                id="account"
                v-model="mobileTillAccountFormData.accountNumber"
                :label="$t('Account')"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :loading="isLoadingAccount"
              />
            </b-col>
            <!-- Account -->
          </b-row>

          <b-row class="match-height">
            <!-- Teller Name -->
            <b-col md="6">
              <SelectField
                id="teller-names"
                v-model="mobileTillAccountFormData.tellerUserID"
                :label="$t('Teller Name')"
                :options="userOptions"
                :reduce="option => option.value"
                :error-name="$t('Teller Name')"
                rules="required"
                :placeholder="$t('Select')"
                item="label"
                :readonly="isReadonly"
                :loading="isLoadingTellerNames"
              />
            </b-col>
            <!-- Teller Name -->
          </b-row>

          <h6 class="section-label mt-2">
            {{ $t('Limit Configurations') }}
          </h6>
          <b-row class="match-height align-items-center">
            <!--   Cash Volume Limit  -->
            <b-col
              md="6"
            >
              <SwitchButton
                v-model="mobileTillAccountFormData.requireCashVolumeLimit"
                :label="$t('Cash Volume Limit?')"
                name="check-button"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
            <!--   Cash Volume Limit  -->
          </b-row>

          <b-row
            v-if="setCashVolumeLimit(mobileTillAccountFormData.requireCashVolumeLimit)"
            class="match-height"
          >

            <b-col md="12">
              <ValidationObserver>
                <b-row>
                  <!--  Max. Balance -->
                  <b-col md="6">
                    <InputNumberField
                      id="max-balance"
                      v-model.number="mobileTillAccountFormData.maxBalance"
                      :label="$t('Max. Balance')"
                      :placeholder="$t('Enter max. balance')"
                      vid="maxFee"
                      :error-name="$t('Max. Balance')"
                      rules="required|integer|positive"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!--  Max. Balance -->

                  <b-col md="6">
                    <InputNumberField
                      id="min-threshold-for-alert"
                      v-model="mobileTillAccountFormData.minThresholdForAlert"
                      :label="$t('Min. Threshold For Alert')"
                      :placeholder="$t('Enter min. threshold for alert')"
                      :error-name="$t('Min. Threshold For Alert')"
                      rules="required|integer|amount:@maxFee|positive"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <!--  Min. Threshold For Alert -->

                </b-row>
              </ValidationObserver>
            </b-col>
            <!--  Min. Threshold For Alert -->

            <!--  Supervisor Email -->
            <b-col
              md="6"
            >
              <InputField
                id="supervisor-email"
                v-model="mobileTillAccountFormData.supervisorEmail"
                :label="$t('Supervisor Email')"
                :placeholder="$t('Enter supervisor email')"
                type="text"
                for="supervisor-email"
                rules="required|email"
                :error-name="$t('Supervisor Email')"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Supervisor Email -->

            <!--  Cash Volume Limit -->
            <!-- <b-col md="6">
              <InputNumberField
                id="cash-volume-limit"
                v-model="mobileTillAccountFormData.cashVolumeLimit"
                label="Cash Volume Limit"
                placeholder="Enter cash volume limit"
                error-name="Cash Volume Limit"
                rules="required|integer|positive"
                :readonly="isReadonly"
              />
            </b-col> -->
            <!--  Cash Volume Limit -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchMobileTillAccount"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            :error-name="$t('Branch')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <!-- Account -->
        <b-col md="6">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            :error-name="$t('Account')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

        <!-- Teller Name -->
        <b-col md="6">
          <SelectField
            id="teller-name"
            v-model="filter.tellerUserID"
            :label="$t('Teller Name')"
            :options="tellerUsersOptions"
            :reduce="option => option.value"
            :error-name="$t('Teller Name')"
            rules="required"
            :placeholder="$t('Select')"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Teller Name -->

        <!--  Supervisor Email -->
        <b-col
          md="6"
        >
          <InputField
            id="supervisor-email"
            v-model="filter.supervisorEmail"
            :label="$t('Supervisor Email')"
            :placeholder="$t('Enter supervisor email')"
            type="text"
            for="supervisor-email"
            rules="required|email"
            :error-name="$t('Supervisor Email')"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Supervisor Email -->

        <!--  Max. Balance -->
        <b-col md="6">
          <InputNumberField
            id="max-balance"
            v-model.number="filter.maxBalance"
            :label="$t('Max. Balance')"
            :placeholder="$t('Enter max. balance')"
            vid="maxFee"
            :error-name="$t('Max. Balance')"
            rules="required|integer|positive"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Max. Balance -->

        <b-col md="6">
          <InputNumberField
            id="min-threshold-for-alert"
            v-model="filter.minThresholdForAlert"
            :label="$t('Min. Threshold For Alert')"
            :placeholder="$t('Enter min. threshold for alert')"
            :error-name="$t('Min. Threshold For Alert')"
            rules="required|integer|amount:@maxFee|positive"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Min. Threshold For Alert -->

        <!--  Cash Volume Limit -->
        <b-col md="6">
          <InputNumberField
            id="cash-volume-limit"
            v-model="filter.cashVolumeLimit"
            :label="$t('Cash Volume Limit')"
            :placeholder="$t('Enter cash volume limit')"
            :error-name="$t('Cash Volume Limit')"
            rules="required|integer|positive"
            :readonly="isReadonly"
          />
        </b-col>
        <!--  Cash Volume Limit -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal,
  BForm, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import { toastAlert } from '@core/mixins/toast'
import { toKobo, toBalance } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SwitchButton,
    TableEntries,
    InputNumberField,
    Button,
    BTable,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    SelectField,
    BDropdown,
    BDropdownItem,
    FullScreenSpinner,
  },
  // mixins: [modalMixins, tableMixins, sweetAlert],
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  data() {
    return {
      sortBy: 'tellerName',
      isReadonly: false,
      isLoadingTellerNames: false,
      required,
      isRequired: false,
      isLoadingAccount: false,
      isLoading: false,
      sortDesc: false,
      isBusy: true,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create New Mobile Till Account'),
      accountUsers: [],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'maxBalance', sortable: true, label: 'Max. Balance' },
        { key: 'minThresholdForAlert', sortable: true, label: 'Min. Threshold For Alert' },
        { key: 'actions', label: 'Actions' },
      ],
      userOptions: [],
      tellerUsersOptions: [],
      mobileTillAccountFormData: {
        id: '',
        branchCode: '',
        accountNumber: '',
        minThresholdForAlert: 0,
        tellerUserID: '',
        maxBalance: 0,
        requireCashVolumeLimit: false,
        supervisorEmail: '',
      },
      requireCashVolumeLimit: false,
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      mobileTillAccountList: [],
      nameOption: [],
      accountTypeOptions: [],
      filter: {
        branchCode: '',
        accountNumber: '',
        tellerUserID: '',
        tellerName: '',
        cashVolumeLimit: '',
        maxBalance: '',
        supervisorEmail: '',
        minThresholdForAlert: '',
        sortColumn: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  mounted() {
    this.fetchMobileTillAccount()
    this.getAccounts()
    this.fetchUsers()
  },
  methods: {
    ...mapActions({
      getMobileTillAccountList: 'operations/getMobileTillAccountList',
      createMobileTillAccount: 'operations/createMobileTillAccountAction',
      updateMobileTillAccount: 'operations/updateMobileTillAccountAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      getUsersByBranchCode: 'operations/getUsersByBranchCode',
      getUsers: 'admin/getUsers',
      deleteMobileTillAccount: 'operations/deleteBusinessAutomation',
    }),
    async getAccounts() {
      try {
        this.isLoadingAccount = true
        const response = await this.getAccountCategoryByCode(10103000)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.accountTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingAccount = false
      }
    },
    async mobileTillAccountAction() {
      try {
        this.isLoading = true
        const formData = {
          id: this.mobileTillAccountFormData.id,
          branchCode: this.mobileTillAccountFormData.branchCode,
          accountNumber: this.mobileTillAccountFormData.accountNumber,
          tellerUserID: this.mobileTillAccountFormData.tellerUserID,
          requireCashVolumeLimit: this.mobileTillAccountFormData.requireCashVolumeLimit,
          supervisorEmail: this.mobileTillAccountFormData.supervisorEmail,
          maxBalance: toKobo(this.mobileTillAccountFormData.maxBalance),
          minThresholdForAlert: toKobo(this.mobileTillAccountFormData.minThresholdForAlert),
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createMobileTillAccount(formData)
          : await this.updateMobileTillAccount(formData)
        const { status } = await response
        if (status === 200) {
          const yourRequest = this.$t('Your request to')
          const TillText = this.$t('Mobile Till account was successfully')
          this.fetchMobileTillAccount()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${TillText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async fetchUsers() {
      try {
        this.isBusy = true
        const response = await this.getUsers({
          search: '',
          page: 0,
          limit: 0,
          permission: operationPermissions.VIEW_BUSINESS_AUTOMATION,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.tellerUsersOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: `${item?.lastname ?? ''} ${item?.firstname ?? ''} ${item.othernames ?? ''}`,
            value: item.id,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectMobileTillAccount(items, activity, val) {
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Mobile Till Account') : this.$t('Mobile Till Account')
      try {
        this.showSpinner = true
        await this.setTellerUsers(items.branchCode)

        this.mobileTillAccountFormData = {
          ...items,
          maxBalance: items.maxBalance !== 0 ? items.maxBalance : '0',
          minThresholdForAlert: items.minThresholdForAlert !== 0 ? items.minThresholdForAlert : '0',
        }
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async setTellerUsers(selectedValue) {
      try {
        this.isLoadingTellerNames = true
        this.userOptions = []
        const response = await this.getUsersByBranchCode(selectedValue)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.userOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: `${item.lastname ? item.lastname : ''} ${item.firstname ? item.firstname : ''} ${item.othernames ? item.othernames : ''}`,
            value: item.id,
          }))
        }
      } catch (error) {
        this.toastAlert({ title: error })
      } finally {
        this.isLoadingTellerNames = false
      }
    },
    async fetchMobileTillAccount() {
      try {
        this.isBusy = true
        const response = await this.getMobileTillAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })

        const { status, data } = response
        if (status === 200 && data.data) {
          this.mobileTillAccountList = data.data.map(item => ({
            ...item,
            cashVolumeLimit: toBalance(item.cashVolumeLimit),
            maxBalance: toBalance(item.maxBalance),
            minThresholdForAlert: toBalance(item.minThresholdForAlert),
          }))
          this.totalCount = data.recordCount
        } else {
          this.mobileTillAccountList = []
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onDeleteMobileTillAccount(item) {
      const deleteMobileTillAccount = async () => {
        try {
          this.showSpinner = true
          const response = await this.deleteMobileTillAccount({ routeName: 'MobileTillAccount', id: item.id })
          const { status } = response
          if (status === 200) {
            this.fetchMobileTillAccount()
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteMobileTillAccount, { text: this.$t('Are you sure you want to delete this Mobile Till Account?') })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.mobileTillAccountRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Mobile Till Account?')
          this.confirm(this.mobileTillAccountAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
        }
      })
    },
    setCashVolumeLimit(value) {
      if (!value) {
        this.mobileTillAccountFormData.maxBalance = 0
        this.mobileTillAccountFormData.minThresholdForAlert = 0
        this.mobileTillAccountFormData.supervisorEmail = ''
        return false
      }
      return true
    },
    resetModal() {
      this.mobileTillAccountFormData = { ...null }
      this.isLoading = false
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchMobileTillAccount()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
